import { SportParser } from "../parser";
import { sportMapperFactory, liveCountMapper, preLiveCountMapper, todayCountMapper, SportsMapper } from "../mapper"
import { SportsRetriever,
    FavoritesRetriever,
    SportPushesRetriever,
    LiveStreamingRetriever,
    LiveStreamingBaseRetriever,
    TodayLiveStreamingRetriever,
    SportsBaseRetriever,
    TodaySportsRetriever
} from "../retriever";
import {
    liveSportBFFQueryFactory,
    liveSportApiQueryFactory,
    preLiveSportApiQueryFactory,
    preLiveSportBFFQueryFactoryConstructor,
    todaySportApiQueryFactory,
    todaySportBFFQueryFactory,
    liveLiveStreamingQueryFactory,
    upcomingLiveStreamingQueryFactory,
    todayLiveStreamingQueryFactory
} from "../queries";
import { BFFGateway, Cookies, SportDataGateway, GlobalConfig, internalMessageBus } from "sb-resp-lib";
import { Retriever, MessageBus, Clock, MessageSender, State, queryType, TimeZoneObservable, ITimeZoneObservable  } from "../../../common";
import { SportModel, sportDictionary, sportConfig} from "..";
import { liveStreamingMode, defaultItemForSportMode } from "../sportConfig";

export const getTimeZoneObservable = ():ITimeZoneObservable => new TimeZoneObservable(getMessageBus());

export const sportsRetrieversFactory = (cfg: sportConfig, dict: sportDictionary, timeZoneObservable: ITimeZoneObservable) => {
    const sportsRetriever = getSportsRetriever(cfg, timeZoneObservable);
    const retrievers: Retriever<SportModel>[] = [sportsRetriever];
    if (cfg.enableFavorites) {
        retrievers.push(new FavoritesRetriever(getMessageBus(), cfg.isFavoritesFirst, cfg.blockId, dict, Cookies));
    }
    if (cfg.enableLiveStreaming) {
        retrievers.push(getLiveStreamingRetriever(cfg, dict, timeZoneObservable));
    }
    return retrievers;
}

const getSportsRetriever = (cfg: sportConfig, timeZoneObservable: ITimeZoneObservable) : Retriever<SportModel> => {
    switch (cfg.query) {
        case queryType.live:
            return getLiveSportsRetriever(cfg);
        case queryType.preLive:
            return getPreLiveSportsRetriever(cfg);
        case queryType.today:
            return getTodaySportRetriever(cfg, timeZoneObservable);
    }
}

const getLiveStreamingRetriever = (cfg: sportConfig, dict: sportDictionary, timeZoneObservable: ITimeZoneObservable) : Retriever<SportModel> => {
    const baseRetriever = new LiveStreamingBaseRetriever(new BFFGateway(), dict, cfg.liveStreamingUpdatePeriod, cfg.liveStreamingMode);
    switch (cfg.query) {
        case queryType.live:
            return new LiveStreamingRetriever(baseRetriever, liveLiveStreamingQueryFactory);
        case queryType.preLive:
            return new LiveStreamingRetriever(baseRetriever, () => upcomingLiveStreamingQueryFactory(new Date()));
        case queryType.today:
            return new TodayLiveStreamingRetriever(baseRetriever, timeZoneObservable, new Clock(Date), todayLiveStreamingQueryFactory);
    }
}

const getLiveSportsRetriever = (cfg: sportConfig) => {
    const bffQueryFactory = liveSportBFFQueryFactory;
    const sportApiQueryFactory = liveSportApiQueryFactory(GlobalConfig.apiConfigs.locale);
    const countMapper = liveCountMapper;
    const sportMapper = sportMapperFactory(countMapper, cfg.sportOrder);
    const sportsMapper = new SportsMapper(sportMapper);
    const sportPushesRetriever = new SportPushesRetriever(sportsMapper, countMapper, new SportDataGateway());
    const baseRetriever = new SportsBaseRetriever(new BFFGateway(), sportsMapper, sportPushesRetriever, cfg.sportRetrieverUpdatePeriod);
    return new SportsRetriever(baseRetriever, bffQueryFactory,sportApiQueryFactory);
}

const getMessageBus = () => new MessageBus(internalMessageBus);

const getPreLiveSportsRetriever = (cfg: sportConfig) => {
    const bffQueryFactory = preLiveSportBFFQueryFactoryConstructor(Date);
    const sportApiQueryFactory = preLiveSportApiQueryFactory(GlobalConfig.apiConfigs.locale, Date);
    const countMapper = preLiveCountMapper;
    const sportMapper = sportMapperFactory(countMapper, cfg.sportOrder);
    const sportsMapper = new SportsMapper(sportMapper);
    const sportPushesRetriever = new SportPushesRetriever(sportsMapper, countMapper, new SportDataGateway());
    const baseRetriever = new SportsBaseRetriever(new BFFGateway(), sportsMapper, sportPushesRetriever, cfg.sportRetrieverUpdatePeriod);
    return new SportsRetriever(baseRetriever, bffQueryFactory, sportApiQueryFactory);
}

const getTodaySportRetriever = (cfg: sportConfig, timeZoneObservable: ITimeZoneObservable) => {
    const countMapper = todayCountMapper;
    const sportMapper = sportMapperFactory(countMapper, cfg.sportOrder);
    const sportsMapper = new SportsMapper(sportMapper);
    const sportPushesRetriever = new SportPushesRetriever(sportsMapper, countMapper, new SportDataGateway());
    const baseRetriever = new SportsBaseRetriever(new BFFGateway(), sportsMapper, sportPushesRetriever, cfg.sportRetrieverUpdatePeriod);
    const sportApiQueryFactory = todaySportApiQueryFactory(GlobalConfig.apiConfigs.locale);
    return new TodaySportsRetriever(baseRetriever, timeZoneObservable, new Clock(Date), todaySportBFFQueryFactory, sportApiQueryFactory);
}

const isFavoritesShouldBeSelected = (cfg: sportConfig) : boolean => {
    return cfg.isFavoritesFirst && cfg.enableFavorites && !isLiveStreamingShouldBeSelected(cfg);
}

const isLiveStreamingShouldBeSelected = (cfg: sportConfig) : boolean => {
    return cfg.enableLiveStreaming && cfg.liveStreamingMode === liveStreamingMode.firstSelected;
}

const getSelectedSportByConfig = (cfg: sportConfig) : defaultItemForSportMode => {
    if (isFavoritesShouldBeSelected(cfg)) {
        return defaultItemForSportMode.favorites;
    }
    if (isLiveStreamingShouldBeSelected(cfg)) {
        return defaultItemForSportMode.liveStreaming;
    }
    return defaultItemForSportMode.firstSport;
}

export const sportsParserFactory = (cfg: sportConfig, retrievers: Retriever<SportModel>[]) => {
    return new SportParser(retrievers, getMessageBus(), cfg.blockId);
}

export const sportsStateFactory = (cfg: sportConfig) => {
    const messageSender = new MessageSender(getMessageBus(), cfg.blockId);
    return new State<SportModel>(messageSender, cfg.blockId, getSelectedSportByConfig(cfg));
}
